import { axiosService } from "@picovico/helpers";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { groupBy } from "lodash";
import { SelectedSeat } from "types/api";
import { AudiDetailResponse } from "types/index";
import { ReduxState } from "../store";

type CurrentSelected = {
  auditorium: string;
  movieHall: string;
  movieShow: string;
  initialSeats: SelectedSeat[];
  selectedSeats: SelectedSeat[];
};

type showDetail = {
  cinemaHall: string;
  screenName: string;
  showIdx: string;
  cinemaHallIdx: string;
  audiIdx: string;
  movieTitle: string;
  movieDate: string;
  movieTime: string;
  grade: string;
  genre: string;
  release_date: string;
  three_d_price?: number;
  location: string;
  language: string;
  duration: number;
  theater: string;
  audi_layout: "col" | "row";
  audiScreenPosition: string;
  seat_selected_time: string | null;
  /** Web ticketing window closed — seat layout is view-only. */
  lockWeb: boolean;
};

type showSeatListInterface = {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  seats: any[];
  prevSelectedSeats: string[];
  prevHoldedSeats: string[];
  prevUserSelectedSeats: string[];
  showDetail: null | showDetail;
  showIdx: null | string;
  currentSelected: CurrentSelected;
};

const initialCurrentSelected: CurrentSelected = {
  auditorium: "",
  movieHall: "",
  movieShow: "",
  initialSeats: [],
  selectedSeats: [],
};

const initialState: showSeatListInterface = {
  isLoading: false,
  isError: false,
  error: null,
  seats: [],
  prevSelectedSeats: [],
  prevHoldedSeats: [],
  prevUserSelectedSeats: [],
  showDetail: null,
  showIdx: null,
  currentSelected: initialCurrentSelected,
};

export const loadSeatsAsync = createAsyncThunk(
  "audi_detail/load",
  async (showIdx: string, { getState, rejectWithValue }) => {
    try {
      const { data } = await axiosService<AudiDetailResponse>({
        name: "show_detail",
        method: "get",
        params: {
          show_idx: showIdx,
          company_idx: (getState() as ReduxState).config.company_idx,
        },
      });

      const audiDetail = data.data;
      const user_selected_seats = audiDetail.user_selected_seats.map((x) => x);
      const groupedSeats = groupBy(audiDetail.seats, "row");
      if (audiDetail) {
        return {
          showIdx,
          user_selected_seats,
          seats: Object.keys(groupedSeats).map((row, rindex) => {
            const orderedSeats = groupedSeats[row].sort((a, b) => {
              return a.column - b.column;
            });

            return {
              row:
                orderedSeats?.find((x) => x.row.toString() == row.toString())
                  ?.row_label || row,
              index: rindex,
              seats: orderedSeats.map((seatItem, cIndex) => {
                return {
                  index: cIndex,
                  seatIdx: seatItem.idx,
                  icon: seatItem.icon,
                  isActive: seatItem.is_active,
                  seat_status: seatItem.seat_status,
                  seatLabel: seatItem.seat_label,
                  ticketType: seatItem.ticket_type,
                  customTicketType: seatItem.custom_ticket_type || null,
                  price: +seatItem.price,
                  row: seatItem.row,
                  column: seatItem.column,
                  canSelect: seatItem.can_select,
                  rowIndex: rindex,
                  colIndex: cIndex,
                };
              }),
            };
          }),
          showDetail: {
            cinemaHall: audiDetail?.cinema_hall?.name,
            cinemaHallIdx: audiDetail?.cinema_hall?.idx as string,
            screenName: audiDetail?.screen_name,
            showIdx,
            audiIdx: audiDetail?.idx as string,
            movieTitle: audiDetail?.movie_detail?.movie_name || "",
            movieDate: audiDetail?.movie_detail?.show_date,
            movieTime: audiDetail?.movie_detail?.show_time,
            grade: audiDetail?.movie_detail?.grade,
            genre: audiDetail?.movie_detail?.genre,
            release_date: audiDetail?.movie_detail?.release_date,
            three_d_price: audiDetail?.three_d_price,
            language: audiDetail?.movie_detail?.language,
            duration: audiDetail?.movie_detail?.duration,
            location: audiDetail?.location || "",
            theater: audiDetail?.cinema_hall?.name || "",
            audi_layout: audiDetail?.audi_layout || "col",
            audiScreenPosition: audiDetail?.screen_position || "Top",
            seat_selected_time: audiDetail.seat_selected_time,
            lockWeb: Boolean(audiDetail?.lock_web),
          },
        };
      }
    } catch (err) {
      return rejectWithValue({ status: (err as { status?: number })?.status });
    }
  },
);

interface uploadSeatPayload {
  seatRow: number;
  seatCol: number;
  canSelect: boolean;
}

type seatActionPayload = {
  movieShow: string;
  seat: SelectedSeat;
};

type initiateBookingPayload = {
  auditorium: string;
  movieShow: string;
  movieHall: string;
};

export const showSeatListSlice = createSlice({
  name: "showSeatList",
  initialState,
  reducers: {
    initiateBooking(state, action: PayloadAction<initiateBookingPayload>) {
      state.currentSelected = {
        ...initialCurrentSelected,
        ...action.payload,
      };
    },
    addSeats(state, action: PayloadAction<seatActionPayload>) {
      if (state.currentSelected.movieShow !== action.payload.movieShow) {
        state.currentSelected = { ...initialCurrentSelected };
        return;
      }
      const alreadySelected = state.currentSelected.selectedSeats.some(
        (seat) => seat.idx === action.payload.seat.idx,
      );
      if (alreadySelected) return;
      state.currentSelected.selectedSeats = [
        ...state.currentSelected.selectedSeats,
        action.payload.seat,
      ];
    },
    removeSeats(state, action: PayloadAction<seatActionPayload>) {
      if (state.currentSelected.movieShow !== action.payload.movieShow) {
        state.currentSelected = { ...initialCurrentSelected };
        return;
      }
      state.currentSelected.selectedSeats =
        state.currentSelected.selectedSeats.filter(
          (seat) => seat.idx !== action.payload.seat.idx,
        );
    },

    updateSeat(state, action: PayloadAction<uploadSeatPayload>) {
      const { seatRow, seatCol, canSelect } = action.payload;
      const seats = [...(state.seats || [])];

      seats[seatRow].seats[seatCol].canSelect = canSelect;
      state.seats = seats;
      return state;
    },
    addBookedSeats(state, action: PayloadAction<string[]>) {
      const seatIdxs = new Set(action.payload || []);
      const seats = [...(state?.seats || [])];
      const prevSeat = new Set(state.prevSelectedSeats.map((p) => p) || []);

      seats.forEach((seatRow) => {
        seatRow.seats = seatRow?.seats?.map(
          (seat: {
            seatIdx: string;
            canSelect: boolean;
            seat_status: string;
          }) => {
            if (seatIdxs.has(seat.seatIdx)) {
              return {
                ...seat,
                canSelect: false,
                seat_status: "booked",
              };
            } else if (
              prevSeat?.has(seat.seatIdx) &&
              !seatIdxs.has(seat.seatIdx)
            ) {
              return {
                ...seat,
                canSelect: true,
                seat_status: "available",
              };
            }
            return seat;
          },
        );
      });

      state.seats = seats;
      state.prevSelectedSeats = Array.from(seatIdxs);
      return state;
    },

    addHoldedSeats(state, action: PayloadAction<string[]>) {
      const seatIdxs = new Set(action.payload || []);
      const seats = [...(state?.seats || [])];
      const prevSeat = new Set(state.prevHoldedSeats.map((d) => d) || []);

      seats.forEach((seatRow) => {
        seatRow.seats = seatRow?.seats?.map(
          (seat: {
            seatIdx: string;
            canSelect: boolean;
            seat_status: string;
          }) => {
            if (seatIdxs.has(seat.seatIdx)) {
              return {
                ...seat,
                canSelect: false,
                seat_status: "hold",
              };
            } else if (
              prevSeat?.has(seat.seatIdx) &&
              !seatIdxs.has(seat.seatIdx)
            ) {
              return {
                ...seat,
                canSelect: true,
                seat_status: "available",
              };
            }
            return seat;
          },
        );
      });

      state.seats = seats;
      state.prevHoldedSeats = Array.from(seatIdxs);
      return state;
    },
    addUserSelectedSeats(state, action: PayloadAction<string[]>) {
      const userSelectedSeats = new Set(action.payload || []);
      const seats = [...(state?.seats || [])];

      const seatDataMap = new Map<string, { idx: string; price: number }>();
      seats.forEach((seatRow) => {
        seatRow.seats = seatRow?.seats?.map(
          (seat: {
            seatIdx: string;
            canSelect: boolean;
            seat_status: string;
            price: number;
          }) => {
            seatDataMap.set(seat.seatIdx, {
              idx: seat.seatIdx,
              price: seat.price,
            });

            if (userSelectedSeats.has(seat.seatIdx)) {
              return {
                ...seat,
                canSelect: true,
                seat_status: "selected",
              };
            }

            return seat;
          },
        );
      });

      state.seats = seats;

      const existingSelectedIdxs = new Set(
        state.currentSelected.selectedSeats.map((s) => s.idx),
      );

      action.payload.forEach((seatIdx) => {
        if (!existingSelectedIdxs.has(seatIdx)) {
          const seatData = seatDataMap.get(seatIdx);
          if (seatData) {
            state.currentSelected.selectedSeats.push(seatData);
          }
        }
      });

      const initialSeatIdxs = new Set(
        state.currentSelected.initialSeats.map((s) => s.idx),
      );
      state.currentSelected.selectedSeats =
        state.currentSelected.selectedSeats.filter(
          (seat) =>
            userSelectedSeats.has(seat.idx) || !initialSeatIdxs.has(seat.idx),
        );

      state.prevUserSelectedSeats = Array.from(userSelectedSeats);

      return state;
    },
    addSeatSelectedTime(state, action: PayloadAction<string>) {
      const inititalSeatSelectionTime = action.payload;
      if (state.showDetail) {
        state.showDetail.seat_selected_time = inititalSeatSelectionTime;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSeatsAsync.pending, (state) => {
        return {
          ...state,
          isLoading: true,
          isError: false,
          error: null,
        };
      })
      .addCase(loadSeatsAsync.fulfilled, (state, action) => {
        const selectedSeats = action.payload?.user_selected_seats;
        return {
          ...state,
          ...action.payload,
          hasData: true,
          isLoading: false,
          currentSelected: {
            ...state.currentSelected,
            initialSeats:
              selectedSeats && selectedSeats.length > 0
                ? [...selectedSeats]
                : state.currentSelected.initialSeats,
            selectedSeats:
              selectedSeats && selectedSeats.length > 0
                ? [...state.currentSelected.selectedSeats, ...selectedSeats]
                : state.currentSelected.selectedSeats,
          },
        };
      })
      .addCase(loadSeatsAsync.rejected, (state, action) => {
        return {
          ...state,
          hasData: false,
          isError: true,
          isLoading: false,
          error: action.payload,
        };
      });
  },
});

export const {
  updateSeat,
  addBookedSeats,
  addHoldedSeats,
  addUserSelectedSeats,
  addSeatSelectedTime,
  initiateBooking,
  addSeats,
  removeSeats,
} = showSeatListSlice.actions;
